import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, IconButton, MenuItem, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  CmpEventLibraryKindEnum,
  useArchiveCmpEventLibraryItemMutation,
  useCreateCmpEventLibraryItemMutation,
  useGetCmpEventLibraryItemsQuery,
  useUpdateCmpEventLibraryItemMutation,
} from "../../../../../graphql/types";
import { FieldLabel, WizardActionButton, wizardTextFieldSx } from "./wizardShared";

// ---------------------------------------------------------------------------
// Shared step-1 popups (verbatim per the "event title/type/code popups"
// screenshots). Exported for reuse by EventTypeSelect and the speaker flow.
// ---------------------------------------------------------------------------

// Add/Edit popup chrome: bold header + grey X, divider, white body, grey
// footer band with an outlined Cancel and a gold confirm button.
export const LibraryEditDialog = ({
  open,
  title,
  confirmLabel,
  confirmDisabled,
  onConfirm,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  confirmLabel: string;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children: React.ReactNode;
}): React.ReactElement => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: { borderRadius: "14px" } }}
  >
    <div className="flex items-center justify-between px-7 pb-4 pt-6">
      <h3 className="text-[22px] font-extrabold leading-tight text-gray-950">{title}</h3>
      <IconButton size="small" onClick={onClose} className="text-gray-400" aria-label="close">
        <CloseIcon />
      </IconButton>
    </div>
    <div className="border-t border-gray-200 px-7 py-7">{children}</div>
    <div className="flex justify-end gap-3 rounded-b-[14px] border-t border-gray-200 bg-gray-50 px-7 py-4">
      <WizardActionButton tone="outline" onClick={onClose}>
        Cancel
      </WizardActionButton>
      <WizardActionButton tone="gold" disabled={confirmDisabled} onClick={onConfirm}>
        {confirmLabel}
      </WizardActionButton>
    </div>
  </Dialog>
);

// Delete confirmation: red circular X close, big red trash circle, verbatim
// heading/body, uppercase DELETE (trash icon) + black uppercase CANCEL.
export const DeleteConfirmModal = ({
  open,
  heading,
  name,
  loading,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  heading: string;
  name: string;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): React.ReactElement => (
  <Dialog
    open={open}
    onClose={onCancel}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: { borderRadius: "18px" } }}
  >
    <DialogContent className="relative !px-8 !py-12">
      <IconButton
        size="small"
        onClick={onCancel}
        aria-label="close"
        className="!absolute !right-4 !top-4"
        sx={{
          backgroundColor: "#ff1728",
          color: "#fff",
          "&:hover": { backgroundColor: "#d90f1e" },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <div className="flex flex-col items-center text-center">
        <div className="mb-7 flex h-24 w-24 items-center justify-center rounded-full bg-[#ff1728] text-white">
          <DeleteOutlineIcon sx={{ fontSize: 52 }} />
        </div>
        <h3 className="text-[30px] font-extrabold leading-tight text-gray-950">{heading}</h3>
        {/* Mock body: `Are you sure you want to delete the {INSERT ...}?` —
            the name is interpolated bare, with no quote marks. */}
        <p className="mt-4 max-w-[420px] text-[19px] leading-8 text-gray-950">
          {`Are you sure you want to delete the ${name}?`}
        </p>
        <div className="mt-9 flex gap-4">
          <WizardActionButton
            tone="red"
            startIcon={<DeleteOutlineIcon />}
            disabled={loading}
            onClick={onConfirm}
            sx={{ minWidth: 150 }}
          >
            DELETE
          </WizardActionButton>
          <WizardActionButton
            tone="black"
            disabled={loading}
            onClick={onCancel}
            sx={{ minWidth: 150 }}
          >
            CANCEL
          </WizardActionButton>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

// Success pop-up: green check circle over a bold title, nothing else.
export const SuccessPopup = ({
  open,
  title,
  onClose,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
}): React.ReactElement => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: { borderRadius: "18px" } }}
  >
    <DialogContent className="!px-8 !py-14">
      <div className="flex flex-col items-center text-center">
        <div className="mb-8 flex h-24 w-24 items-center justify-center rounded-full bg-green-500 text-white">
          <CheckCircleOutlineIcon sx={{ fontSize: 56 }} />
        </div>
        <h3 className="text-[28px] font-extrabold leading-tight text-gray-950">{title}</h3>
      </div>
    </DialogContent>
  </Dialog>
);

// ---------------------------------------------------------------------------
// Event Title / Event Code library select
// ---------------------------------------------------------------------------

type EventLibrarySelectProps = {
  chamberId: string;
  kind: CmpEventLibraryKindEnum;
  label: string;
  hint?: string;
  required?: boolean;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
};

const noun = (kind: CmpEventLibraryKindEnum) =>
  kind === CmpEventLibraryKindEnum.Title ? "Event Title" : "Event Code";
const shortNoun = (kind: CmpEventLibraryKindEnum) =>
  kind === CmpEventLibraryKindEnum.Title ? "Title" : "Code";

const EventLibrarySelect = ({
  chamberId,
  kind,
  label,
  hint,
  required,
  placeholder,
  value,
  onChange,
  error,
}: EventLibrarySelectProps): React.ReactElement => {
  const { data, refetch } = useGetCmpEventLibraryItemsQuery({
    variables: { input: { chamberId, kind, includeArchived: false } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const items = useMemo(
    () => data?.getCmpEventLibraryItems.items || [],
    [data]
  );
  const selected = items.find((item) => item.value === value);

  const [createItem, { loading: creating }] =
    useCreateCmpEventLibraryItemMutation();
  const [updateItem, { loading: updating }] =
    useUpdateCmpEventLibraryItemMutation();
  const [archiveItem, { loading: archiving }] =
    useArchiveCmpEventLibraryItemMutation();

  const [dialog, setDialog] = useState<"add" | "edit" | null>(null);
  const [draftValue, setDraftValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const openAdd = () => {
    setDraftValue("");
    setDialog("add");
  };
  const openEdit = () => {
    if (!selected && !value) {
      return;
    }
    setDraftValue(selected?.value || value);
    setDialog("edit");
  };

  const handleSave = async () => {
    if (!draftValue.trim()) {
      return;
    }
    if (dialog === "add") {
      const created = await createItem({
        variables: { input: { chamberId, kind, value: draftValue.trim() } },
      });
      await refetch();
      const createdValue =
        created.data?.createCmpEventLibraryItem?.item?.value || draftValue.trim();
      onChange(createdValue);
    } else if (dialog === "edit" && selected) {
      await updateItem({
        variables: {
          input: { chamberId, itemId: selected.id, value: draftValue.trim() },
        },
      });
      await refetch();
      onChange(draftValue.trim());
    }
    setDialog(null);
  };

  const handleDelete = async () => {
    if (!selected) {
      return;
    }
    await archiveItem({
      variables: { input: { chamberId, itemId: selected.id } },
    });
    await refetch();
    onChange("");
    setConfirmDelete(false);
    setResult(`${noun(kind)} Deleted Successfully!`);
  };

  return (
    <div>
      <FieldLabel required={required} hint={hint}>
        {label}
      </FieldLabel>
      <TextField
        select
        fullWidth
        size="small"
        value={value}
        onChange={({ target }) => onChange(target.value)}
        error={Boolean(error)}
        helperText={error}
        sx={wizardTextFieldSx}
        SelectProps={{
          displayEmpty: true,
          renderValue: () =>
            value ? (
              // The selected Event Title previews in the same serif display
              // face the public event page renders titles in (July 9, 2026
              // direction); codes and empty placeholders stay in the app font.
              <span
                className={
                  kind === CmpEventLibraryKindEnum.Title
                    ? "font-serif font-bold"
                    : undefined
                }
              >
                {value}
              </span>
            ) : (
              <span className="text-gray-500">{placeholder || `Select ${noun(kind)}`}</span>
            ),
        }}
      >
        {items.length === 0 ? (
          <MenuItem value="">{`No saved ${noun(kind).toLowerCase()}s yet`}</MenuItem>
        ) : null}
        {value && !selected ? <MenuItem value={value}>{value}</MenuItem> : null}
        {items.map((item) => (
          <MenuItem value={item.value} key={item.id}>
            {item.value}
          </MenuItem>
        ))}
      </TextField>
      <div className="mt-2 flex gap-2">
        <WizardActionButton tone="black" size="small" onClick={openAdd}>
          Add {noun(kind)}
        </WizardActionButton>
        <WizardActionButton
          tone="gold"
          size="small"
          startIcon={<EditOutlinedIcon />}
          onClick={openEdit}
        >
          Edit
        </WizardActionButton>
        <WizardActionButton
          tone="red"
          size="small"
          startIcon={<DeleteOutlineIcon />}
          onClick={() => selected && setConfirmDelete(true)}
        >
          Delete
        </WizardActionButton>
      </div>

      <LibraryEditDialog
        open={Boolean(dialog)}
        title={dialog === "edit" ? `Edit ${noun(kind)}` : `Add ${noun(kind)}`}
        confirmLabel={
          dialog === "edit" ? `Edit ${shortNoun(kind)}` : `Save ${shortNoun(kind)}`
        }
        confirmDisabled={creating || updating || !draftValue.trim()}
        onConfirm={handleSave}
        onClose={() => setDialog(null)}
      >
        <TextField
          fullWidth
          size="small"
          placeholder={`Add ${noun(kind)}`}
          value={draftValue}
          onChange={({ target }) => setDraftValue(target.value)}
          sx={wizardTextFieldSx}
        />
      </LibraryEditDialog>

      <DeleteConfirmModal
        open={confirmDelete}
        heading={`Delete ${noun(kind)}`}
        name={selected?.value || value}
        loading={archiving}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <SuccessPopup
        open={Boolean(result)}
        title={result || ""}
        onClose={() => setResult(null)}
      />
    </div>
  );
};

export default EventLibrarySelect;
